import React, { useContext, useEffect, useState } from 'react';
import { OptInDialog } from '../opt-in';
import { AddYourDeviceDialog } from '../opt-in/add-your-device';
import { AddYourDeviceConfirmationDialog } from '../opt-in/add-your-device-confirmation';
import { DeviceSyncContext } from '../../context/DeviceSyncContext';
import { useRouter } from 'next/router';
import _ from 'lodash';
import { OptInContext } from '../../context/OptInContext';
import { DeviceTypeEnum } from '../../types/device-type.enum';
import { OptInDeviceType } from '../../types/opt-in-device.type';
import { TrackingContext } from '../../context/TrackingContext';
import { TrackingEvent } from '../../types/tracking-events.enum';
import useAuth from '../../auth/use-auth';
import { useTranslation } from 'react-i18next';
import { isAnyOfTheBookingsOwp } from '../../util/booking';
import { Welcome } from '../opt-in/welcome';

interface Props {
    children: React.ReactNode;
}

type OptInDialogOption = {
    optInOptionSelected: boolean;
    memberId: string | undefined;
};

export default function OptInDialogWrapper(props: Props) {
    const optInLocalStorageKey =
        process.env.NEXT_PUBLIC_OPT_IN_LOCAL_STORAGE_KEY;
    const [openAddYourDeviceDialog, setOpenAddYourDeviceDialog] = useState(
        false,
    );
    const [openWelcomeDialog, setOpenWelcomeDialog] = useState(false);
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const { syncDevices } = useContext(DeviceSyncContext);
    const { user } = useAuth();
    const { t } = useTranslation();
    const optInContext = {
        openAddYourDeviceDialog,
        setOpenAddYourDeviceDialog,
        openConfirmationDialog,
        setOpenConfirmationDialog,
    };
    const router = useRouter();
    const whoopDevice: OptInDeviceType = {
        logoFileName: 'whoop.svg',
        type: DeviceTypeEnum.WHOOP,
        description: t('optInDialogWrapper.deviceType.description'),
    };
    const { sendEvent } = useContext(TrackingContext);

    const isOptInDialogOptionSelected = (): boolean => {
        if (optInLocalStorageKey) {
            const rawStoredValue = localStorage.getItem(optInLocalStorageKey);
            if (rawStoredValue) {
                const optInDialogOption: OptInDialogOption = JSON.parse(
                    rawStoredValue,
                );
                return (
                    optInDialogOption.optInOptionSelected &&
                    optInDialogOption.memberId === user?.memberId
                );
            }
            return false;
        }
        return false;
    };

    useEffect(() => {
        if (
            !_.isString(router.query.success) &&
            user?.memberId &&
            !user.devices?.length &&
            user.termsAndConditions?.accepted &&
            user.memberId !== user.membersPortalEmailAddress &&
            !isOptInDialogOptionSelected()
        ) {
            setOpenWelcomeDialog(true);
            sendEvent(TrackingEvent.OPT_IN_DIALOG_OPENED);
        } else if (user?.memberId && _.isString(router.query.success)) {
            setOpenConfirmationDialog(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router, user]);

    const handleDeviceTypeClick = (device: OptInDeviceType) => {
        dismissOptInDialog();
        const state = {
            EmailAddress: user?.membersPortalEmailAddress,
            RedirectTo: window.location.href,
        };
        window.location.href =
            process.env.NEXT_PUBLIC_WHOOP_AUTH_URI +
            '?state=' +
            JSON.stringify(state);
    };

    const dismissOptInDialog = () => {
        const valueToStore = {
            optInOptionSelected: true,
            memberId: user?.memberId,
        };
        storeOptInValue(valueToStore);
        setOpenAddYourDeviceDialog(false);
        setOpenWelcomeDialog(false);
        sendEvent(TrackingEvent.OPT_IN_DIALOG_CLOSED);
    };

    const storeOptInValue = (optInDialogOption: OptInDialogOption) => {
        if (optInLocalStorageKey) {
            localStorage.setItem(
                optInLocalStorageKey,
                JSON.stringify(optInDialogOption),
            );
        }
    };

    const removeQueryParameter = (): Promise<boolean> => {
        return router.push(window.location.href.split('?')[0], undefined, {
            shallow: true,
        });
    };

    const handleFinishClick = () => {
        removeQueryParameter().then(() => {
            setOpenConfirmationDialog(false);
            syncDevices([DeviceTypeEnum.WHOOP], user);
        });
        sendEvent(TrackingEvent.OPT_IN_DIALOG_CLOSED);
    };

    const handleTryAgainClick = () => {
        removeQueryParameter().then(() => {
            setOpenConfirmationDialog(false);
            setOpenAddYourDeviceDialog(true);
        });
    };

    const handleWelcomeButton = () => {
        isAnyOfTheBookingsOwp(user?.bookings)
            ? setOpenAddYourDeviceDialog(true)
            : dismissOptInDialog();
    };

    const getWelcomeDrawerButtonText = (): string => {
        return isAnyOfTheBookingsOwp(user?.bookings)
            ? t('optInWelcome.next')
            : t('optInWelcome.ok');
    };

    return (
        <OptInContext.Provider value={optInContext}>
            <OptInDialog
                key={'AddYourDeviceConfirmationDialog'}
                open={openConfirmationDialog}
                onClose={() => handleFinishClick()}
                title={
                    router.query.success === 'true'
                        ? t('optInDialogWrapper.connection.success')
                        : t('optInDialogWrapper.connection.failed')
                }
            >
                <AddYourDeviceConfirmationDialog
                    status={router.query.success === 'true'}
                    device={whoopDevice}
                    onFinishClick={() => {
                        sendEvent(TrackingEvent.OPT_IN_FINISH_CLICKED);
                        handleFinishClick();
                    }}
                    onTryAgainClick={() => {
                        sendEvent(TrackingEvent.OPT_IN_TRY_AGAIN_CLICKED);
                        handleTryAgainClick();
                    }}
                />
            </OptInDialog>
            <OptInDialog
                key={'WelcomeDialog'}
                imgSrc={'opt-in.png'}
                open={openWelcomeDialog}
                onClose={() => {
                    dismissOptInDialog();
                }}
                title={t('optInDialogWrapper.welcome.title')}
            >
                <Welcome
                    buttonText={getWelcomeDrawerButtonText()}
                    onButtonClick={handleWelcomeButton}
                />
            </OptInDialog>
            <OptInDialog
                key={'AddYourDeviceDialog'}
                open={openAddYourDeviceDialog}
                onClose={() => {
                    dismissOptInDialog();
                }}
                title={t('optInDialogWrapper.title')}
                description={t('optInDialogWrapper.description')}
                imgSrc={'wearable.png'}
            >
                <AddYourDeviceDialog
                    onDeviceClick={(device) => {
                        sendEvent(TrackingEvent.OPT_IN_DEVICE_SELECTED, {
                            device: device.type,
                        });
                        handleDeviceTypeClick(device);
                    }}
                    onOptInLaterClick={() => {
                        sendEvent(TrackingEvent.OPT_IN_LATER_CLICKED);
                        dismissOptInDialog();
                    }}
                    device={whoopDevice}
                />
            </OptInDialog>
            {props.children}
        </OptInContext.Provider>
    );
}
